import {
  deriveResidentDocumentStatusLabel,
  getResidentDocumentsForType,
  type ResidentUploadedDocumentRow,
} from '@/features/resident/utils/residentDocuments'
import type { ResidentDefaultFormValues, ResidentDocumentGroup } from '@/features/resident/types'
import type {
  ReimbursementDocumentFile,
  ReimbursementDocumentReviewStatus,
  ReimbursementDocumentType,
  ReimbursementDocumentsMap,
} from '../types/reimbursementFormTypes'
import {
  createEmptyReimbursementDocumentsMap,
  DOCUMENT_TYPE_LABELS,
} from '../types/reimbursementFormTypes'
import { resolveReimbursementDocumentTypeLabel } from './reimbursementDocumentReviewCatalog'

export interface ReclassifyOption {
  value: string
  label: string
  occupied?: boolean
}

export type ReimbursementDocumentGroupKey =
  | 'lease_agreement'
  | 'original_lease_agreement'
  | 'payment_ledger'
  | 'original_payment_ledger'
  | 'final_payment_ledger'
  | 'skip_claim_affidavit'
  | 'eviction_lawsuit'
  | 'notice_to_vacate'
  | 'alternate_court_documents'
  | 'bankruptcy_petition'
  | 'appeal_document'
  | 'court_registry_rent_bond'
  | 'stipulation_agreement'
  | 'default_stipulation'

export type ReimbursementImmediateUploadField =
  | 'lease_agreement'
  | 'original_lease_agreement'
  | 'payment_ledger'
  | 'original_payment_ledger'
  | 'final_payment_ledger'
  | 'eviction_lawsuit'
  | 'skip_claim_affidavit'
  | 'notice_to_vacate'
  | 'alternate_court_documents'
  | 'bankruptcy_petition'
  | 'appeal_document'
  | 'court_registry_rent_bond'
  | 'stipulation_agreement'
  | 'default_stipulation'

export const API_DOC_TYPE_MAP: Partial<
  Record<
    ReimbursementDocumentType,
    {
      documentGroupKey: ReimbursementDocumentGroupKey
      uploadField: ReimbursementImmediateUploadField
      statusKey: keyof ResidentDefaultFormValues
      fileKey: ReimbursementImmediateUploadField
    }
  >
> = {
  signed_lease: {
    documentGroupKey: 'lease_agreement',
    uploadField: 'lease_agreement',
    statusKey: 'lease_agreement_status',
    fileKey: 'lease_agreement',
  },
  original_lease: {
    documentGroupKey: 'original_lease_agreement',
    uploadField: 'original_lease_agreement',
    statusKey: 'lease_agreement_status',
    fileKey: 'lease_agreement',
  },
  current_ledger: {
    documentGroupKey: 'payment_ledger',
    uploadField: 'payment_ledger',
    statusKey: 'payment_ledger_status',
    fileKey: 'payment_ledger',
  },
  original_ledger: {
    documentGroupKey: 'original_payment_ledger',
    uploadField: 'original_payment_ledger',
    statusKey: 'payment_ledger_status',
    fileKey: 'payment_ledger',
  },
  final_ledger: {
    documentGroupKey: 'final_payment_ledger',
    uploadField: 'final_payment_ledger',
    statusKey: 'payment_ledger_status',
    fileKey: 'payment_ledger',
  },
  skip_affidavit: {
    documentGroupKey: 'skip_claim_affidavit',
    uploadField: 'skip_claim_affidavit',
    statusKey: 'skip_claim_affidavit_status',
    fileKey: 'skip_claim_affidavit',
  },
  eviction_lawsuit: {
    documentGroupKey: 'eviction_lawsuit',
    uploadField: 'eviction_lawsuit',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'eviction_lawsuit',
  },
  alternate_court_document: {
    documentGroupKey: 'alternate_court_documents',
    uploadField: 'alternate_court_documents',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'alternate_court_documents',
  },
  cares_act_notice: {
    documentGroupKey: 'notice_to_vacate',
    uploadField: 'notice_to_vacate',
    statusKey: 'lease_agreement_status',
    fileKey: 'lease_agreement',
  },
  bankruptcy_document: {
    documentGroupKey: 'bankruptcy_petition',
    uploadField: 'bankruptcy_petition',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'bankruptcy_petition',
  },
  court_registry: {
    documentGroupKey: 'appeal_document',
    uploadField: 'appeal_document',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'appeal_document',
  },
  bond_registry_receipt: {
    documentGroupKey: 'court_registry_rent_bond',
    uploadField: 'court_registry_rent_bond',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'court_registry_rent_bond',
  },
  stipulation_agreement: {
    documentGroupKey: 'stipulation_agreement',
    uploadField: 'stipulation_agreement',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'stipulation_agreement',
  },
  default_stipulation: {
    documentGroupKey: 'default_stipulation',
    uploadField: 'default_stipulation',
    statusKey: 'unlawful_detainer_status',
    fileKey: 'default_stipulation',
  },
}

function normalizeReviewStatus(label: string): ReimbursementDocumentReviewStatus {
  const normalized = String(label ?? '').trim().toLowerCase()
  if (!normalized || normalized === 'missing') return 'pending'
  if (['received', 'approved', 'complete', 'completed', 'paid'].includes(normalized)) return 'approved'
  if (['incomplete', 'rejected', 'denied'].includes(normalized)) return 'incomplete'
  if (['uploaded', 'pending'].includes(normalized)) return 'uploaded'
  return 'uploaded'
}

/** PMC/LR: no delete after Liberty review (matches DocumentApprovalControls remove gate). */
export function isUploadedDocumentRemoveLockedAfterReview(actionOrStatus?: unknown): boolean {
  const normalized = String(actionOrStatus ?? '').trim().toLowerCase()
  if (!normalized) return false
  return (
    normalized === 'approved' ||
    normalized === 'received' ||
    normalized === 'incomplete' ||
    normalized === 'rejected'
  )
}

export function canRemoveUploadedDocumentAfterReview(args: {
  allowRemove: boolean
  action?: unknown
  status?: ReimbursementDocumentReviewStatus
}): boolean {
  if (!args.allowRemove) return false
  if (args.status && isUploadedDocumentRemoveLockedAfterReview(args.status)) return false
  if (args.action != null && String(args.action).trim() !== '') {
    return !isUploadedDocumentRemoveLockedAfterReview(args.action)
  }
  return true
}

/** Status pill for a doc row: latest attachment `action` wins over shared form `*_status` columns. */
export function deriveDocumentStatusLabelFromRows(
  rows: ResidentUploadedDocumentRow[],
): string {
  if (!rows.length) return ''
  const current = getCurrentDocumentRow(rows)
  const action = String(current?.action ?? '').trim()
  if (action) {
    return action === 'REJECTED' ? 'INCOMPLETE' : action
  }
  return deriveResidentDocumentStatusLabel(rows)
}

export function resolveReimbursementDocumentStatusLabel(
  values: ResidentDefaultFormValues,
  docType: ReimbursementDocumentType,
  residentDocuments?: ResidentDocumentGroup | null,
): string {
  const config = API_DOC_TYPE_MAP[docType]
  if (!config) return ''
  const rows = getUploadedRowsForDocType(residentDocuments, docType)
  const fromAttachment = deriveDocumentStatusLabelFromRows(rows)
  if (fromAttachment) return fromAttachment
  return String(values[config.statusKey] ?? '').trim()
}

function parseDocumentUploadDate(
  row: { created_at?: string | number; filedate?: string | number },
): Date | undefined {
  const createdAt = row.created_at
  if (createdAt != null && String(createdAt).trim() !== '') {
    const numeric = Number(createdAt)
    if (Number.isFinite(numeric)) {
      return new Date(String(createdAt).length >= 13 ? numeric : numeric * 1000)
    }
  }
  const filedate = row.filedate
  if (filedate == null || String(filedate).trim() === '') return undefined
  const raw = String(filedate).trim()
  if (/^\d{10,13}$/.test(raw)) {
    const numeric = Number(raw)
    return new Date(raw.length >= 13 ? numeric : numeric * 1000)
  }
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

function hasReviewActor(actionBy: unknown): boolean {
  const trimmed = String(actionBy ?? '').trim()
  return trimmed !== '' && trimmed !== ' '
}

function attachmentSortKey(row: {
  attachment_id?: string | number
  reimbursement_attachment_id?: string | number
  filedate?: string | number
  created_at?: string | number
}): number {
  const id = Number(row.reimbursement_attachment_id ?? row.attachment_id ?? 0)
  if (Number.isFinite(id) && id > 0) return id
  const filedate = Number(row.filedate ?? row.created_at ?? 0)
  return Number.isFinite(filedate) ? filedate : 0
}

function isIncompleteAttachmentAction(action: unknown): boolean {
  const normalized = String(action ?? '').trim().toLowerCase()
  return normalized === 'incomplete' || normalized === 'rejected'
}

export function isApprovedAttachmentAction(action: unknown): boolean {
  const normalized = String(action ?? '').trim().toLowerCase()
  return normalized === 'approved' || normalized === 'received'
}

export function getApprovedAttachmentRows(
  rows: ResidentUploadedDocumentRow[],
): ResidentUploadedDocumentRow[] {
  return sortDocumentRowsChronologically(rows).filter((row) =>
    isApprovedAttachmentAction(row.action),
  )
}

/** API returns attachment history oldest-first; the active file is the latest row. */
export function sortDocumentRowsChronologically<T extends ResidentUploadedDocumentRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => attachmentSortKey(a) - attachmentSortKey(b))
}

export function getCurrentDocumentRow(
  rows: ResidentUploadedDocumentRow[],
): ResidentUploadedDocumentRow | undefined {
  const sorted = sortDocumentRowsChronologically(rows)
  return sorted[sorted.length - 1]
}

export function getPreviousIncompleteDocumentRow(
  rows: ResidentUploadedDocumentRow[],
  currentRow?: ResidentUploadedDocumentRow,
): ResidentUploadedDocumentRow | undefined {
  const sorted = sortDocumentRowsChronologically(rows)
  const current = currentRow ?? sorted[sorted.length - 1]
  if (!current) return undefined
  for (let index = sorted.length - 2; index >= 0; index -= 1) {
    if (isIncompleteAttachmentAction(sorted[index]?.action)) {
      return sorted[index]
    }
  }
  return undefined
}

export function enrichReimbursementDocumentWithPreviousSubmission(
  file: ReimbursementDocumentFile,
  previousRow: ResidentUploadedDocumentRow,
): ReimbursementDocumentFile {
  const reviewedBy = hasReviewActor(previousRow.action_by)
    ? String(previousRow.action_by).trim()
    : undefined
  const reviewedAt =
    reviewedBy && previousRow.action_date != null && String(previousRow.action_date).trim() !== ''
      ? parseDocumentUploadDate({ filedate: previousRow.action_date })
      : undefined

  return {
    ...file,
    previousFileName: String(previousRow.filename ?? '').trim() || undefined,
    previousFileUrl:
      String(previousRow.filepath ?? previousRow.file_path ?? '').trim() || undefined,
    previousRejectionReason: String(previousRow.reason ?? '').trim() || undefined,
    previousReviewedAt: reviewedAt,
    previousReviewedBy: reviewedBy,
  }
}

export function getReclassifyDisplayFromRow(row: {
  reclassify?: string
  reclassify_by?: string | number
  reclassify_date?: string | number
}): Pick<ReimbursementDocumentFile, 'reclassifiedFrom' | 'reclassifiedAt' | 'reclassifiedBy'> {
  const reclassifySource = String(row.reclassify ?? '').trim()
  if (!reclassifySource) {
    return {}
  }
  const reclassifiedBy =
    row.reclassify_by != null && String(row.reclassify_by).trim() !== ''
      ? String(row.reclassify_by).trim()
      : undefined
  const reclassifiedAt =
    row.reclassify_date != null && String(row.reclassify_date).trim() !== ''
      ? parseDocumentUploadDate({ filedate: row.reclassify_date })
      : undefined

  return {
    reclassifiedFrom: resolveReimbursementDocumentTypeLabel(reclassifySource),
    reclassifiedAt,
    reclassifiedBy,
  }
}

function rowToDocumentFile(
  row: {
    attachment_id?: string | number
    reimbursement_attachment_id?: string | number
    filename?: string
    filepath?: string
    file_path?: string
    created_at?: string | number
    filedate?: string | number
    uploaded_by?: string
    action_by?: string
    action_date?: string | number
    action?: string
    reason?: string
    reclassify?: string
    reclassify_by?: string | number
    reclassify_date?: string | number
  },
  statusLabel: string,
): ReimbursementDocumentFile {
  const reviewedBy = hasReviewActor(row.action_by) ? String(row.action_by).trim() : undefined
  const reviewedAt =
    reviewedBy && row.action_date != null && String(row.action_date).trim() !== ''
      ? parseDocumentUploadDate({ filedate: row.action_date })
      : undefined

  const reclassifyDisplay = getReclassifyDisplayFromRow(row)

  const actionLabel = String(row.action ?? statusLabel ?? '').trim()
  const status = normalizeReviewStatus(actionLabel || statusLabel)
  const rejectionReason =
    status === 'incomplete'
      ? String(row.reason ?? '').trim() || undefined
      : undefined

  const attachmentId =
    row.reimbursement_attachment_id ?? row.attachment_id ?? undefined

  return {
    id: attachmentId != null ? String(attachmentId) : '',
    file: null,
    fileName: String(row.filename ?? 'Uploaded file'),
    fileSize: 0,
    uploadedAt: parseDocumentUploadDate(row),
    uploadedBy: String(row.uploaded_by ?? '').trim(),
    status,
    reviewedAt,
    reviewedBy,
    rejectionReason,
    reclassifiedFrom: reclassifyDisplay.reclassifiedFrom,
    reclassifiedAt: reclassifyDisplay.reclassifiedAt,
    reclassifiedBy: reclassifyDisplay.reclassifiedBy,
  }
}

export function mapUploadRowToReimbursementDocumentFile(
  row: ResidentUploadedDocumentRow,
): ReimbursementDocumentFile {
  return rowToDocumentFile(row, String(row.action ?? 'UPLOADED'))
}

export function getDocumentReviewMetadataFromUploadRow(row: ResidentUploadedDocumentRow) {
  const file = mapUploadRowToReimbursementDocumentFile(row)
  return {
    uploadedAt: file.uploadedAt,
    uploadedBy: file.uploadedBy,
    status: file.status,
    reviewedAt: file.reviewedAt,
    reviewedBy: file.reviewedBy,
    rejectionReason: file.rejectionReason,
    reclassifiedFrom: file.reclassifiedFrom,
    reclassifiedAt: file.reclassifiedAt,
    reclassifiedBy: file.reclassifiedBy,
  }
}

export function getUploadedRowsForDocType(
  residentDocuments: ResidentDocumentGroup | null | undefined,
  docType: ReimbursementDocumentType,
) {
  const config = API_DOC_TYPE_MAP[docType]
  if (!config || !residentDocuments) return []
  return getResidentDocumentsForType(residentDocuments, config.documentGroupKey)
}

export function resolveAttachmentIdForDocType(
  docType: ReimbursementDocumentType,
  document: ReimbursementDocumentFile | null | undefined,
  residentDocuments?: ResidentDocumentGroup | null,
): string | number | undefined {
  const fromLocal = document?.id?.trim()
  if (fromLocal) return fromLocal
  const rows = getUploadedRowsForDocType(residentDocuments, docType)
  const row = getCurrentDocumentRow(rows)
  if (!row) return undefined

  return row.reimbursement_attachment_id ?? row.attachment_id ?? row.id
}

export function syncApiDocumentsToLocalMap(
  values: ResidentDefaultFormValues,
  residentDocuments?: ResidentDocumentGroup | null,
  existingLocal?: ReimbursementDocumentsMap,
): ReimbursementDocumentsMap {
  const map = existingLocal ? { ...existingLocal } : createEmptyReimbursementDocumentsMap()
  const apiAuthoritative = residentDocuments != null

  for (const [docType, config] of Object.entries(API_DOC_TYPE_MAP) as Array<
    [ReimbursementDocumentType, NonNullable<(typeof API_DOC_TYPE_MAP)[ReimbursementDocumentType]>]
  >) {
    const filteredRows = getResidentDocumentsForType(residentDocuments, config.documentGroupKey)

    const statusLabel =
      deriveDocumentStatusLabelFromRows(filteredRows) ||
      String(values[config.statusKey] ?? '').trim() ||
      (filteredRows.length ? deriveResidentDocumentStatusLabel(filteredRows) : '')

    const hasFormFile = Boolean(
      String((values as unknown as Record<string, unknown>)[config.fileKey] ?? '').trim(),
    )
    const currentRow = getCurrentDocumentRow(filteredRows)
    if (currentRow) {
      let file = rowToDocumentFile(currentRow, statusLabel)
      const previousRow = getPreviousIncompleteDocumentRow(filteredRows, currentRow)
      if (previousRow) {
        file = enrichReimbursementDocumentWithPreviousSubmission(file, previousRow)
      }
      map[docType] = file
    } else if (hasFormFile && !apiAuthoritative) {
      map[docType] = {
        file: null,
        fileName: String((values as unknown as Record<string, unknown>)[config.fileKey] ?? 'Uploaded file'),
        fileSize: 0,
        status: normalizeReviewStatus(statusLabel),
      }
    } else {
      map[docType] = null
    }
  }

  return map
}

export function getApiUploadFieldForDocType(
  docType: ReimbursementDocumentType,
): { fieldName: ReimbursementImmediateUploadField } | null {
  const config = API_DOC_TYPE_MAP[docType]
  if (!config) return null
  return { fieldName: config.uploadField }
}

export function getFormStatusKeyForDocType(
  docType: ReimbursementDocumentType,
): keyof ResidentDefaultFormValues | null {
  return API_DOC_TYPE_MAP[docType]?.statusKey ?? null
}

export function isDocTypeOccupied(
  docType: ReimbursementDocumentType,
  localDocuments: ReimbursementDocumentsMap,
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  if (localDocuments[docType]) return true
  return getUploadedRowsForDocType(residentDocuments, docType).length > 0
}

export function buildReclassifyOptions(input: {
  fromDocType: ReimbursementDocumentType
  enabledDocTypes: ReimbursementDocumentType[]
  localDocuments: ReimbursementDocumentsMap
  residentDocuments?: ResidentDocumentGroup | null
}): ReclassifyOption[] {
  const { fromDocType, enabledDocTypes, localDocuments, residentDocuments } = input
  if (fromDocType === 'skip_affidavit') return []

  return enabledDocTypes
    .filter((target) => target !== fromDocType && target !== 'skip_affidavit')
    .map((target) => ({
      value: target,
      label: DOCUMENT_TYPE_LABELS[target],
      occupied: isDocTypeOccupied(target, localDocuments, residentDocuments),
    }))
}
